import { useNavigate } from "@tanstack/react-router";
import { CheckCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Post-completion confirmation screen for the trainee player (CHP-443), styled
 * to match the other confirmation pages (mirrors `DealConfirmation`).
 *
 * Three states, decided by the caller off the freshly-refetched me-status:
 * - `certified` — a gate-eligible BDM whose completion cleared the LAST
 *   outstanding compulsory module: the full certification / deal-reg unlock
 *   screen with both buttons.
 * - `keep-going` — a gate-eligible BDM who finished a compulsory module but has
 *   others still outstanding: no unlock claim, just "Continue Training".
 * - `neutral` — anyone else (PO/manager, or any optional-module completion).
 *
 * Copy ownership: the `certified` heading + body + button labels are
 * designer-provided (verbatim). The `keep-going` / `neutral` wording is
 * proposed — confirm with the designer against the reference image at the walk
 * (feedback_designer_doc_ownership).
 */
export type TrainingCompletionVariant = "certified" | "keep-going" | "neutral";

export function TrainingCompletionConfirmation({
  variant,
  moduleName,
}: {
  variant: TrainingCompletionVariant;
  moduleName: string;
}) {
  const navigate = useNavigate();
  const trainingTo = "/dashboard/partner/training";
  const certified = variant === "certified";

  return (
    <div className="container py-6">
      <Card className="mx-auto max-w-lg" data-testid="training-completion-confirmation">
        <CardHeader className="items-center text-center">
          <CheckCircle className="mb-2 h-12 w-12 text-success" aria-hidden />
          <CardTitle>
            {certified ? "Deal registration unlocked" : `${moduleName} Module complete`}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-center text-sm">
          {certified ? (
            <p>You are now certified and ready to register new business.</p>
          ) : variant === "keep-going" ? (
            <p className="text-muted-foreground">
              Great progress. Complete the remaining mandatory modules to unlock deal registration.
            </p>
          ) : (
            <p className="text-muted-foreground">Nice work — you&apos;ve completed this module.</p>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            {certified ? (
              <Button onClick={() => void navigate({ to: "/dashboard/partner/deals/new" })}>
                Register a deal
              </Button>
            ) : null}
            <Button
              variant={certified ? "outline" : "default"}
              onClick={() => void navigate({ to: trainingTo })}
            >
              {certified ? "View elective training" : "Continue training"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
